import React, { useState, useCallback } from "react";
import {
  Alert,
  Badge,
  Card,
  Loader,
  Pagination,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconListCheck,
  IconLock,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import {
  useAllRejectedQaTasks,
  useNeedsReview,
  qaReviewConfigFor,
  type QaTaskRow,
} from "@/hooks/useMyRejectedQaTasks";

const taskUrl = ({ task, config }: QaTaskRow) =>
  `/jobs/${task.job_id}/${config.feedbackRoute}?taskId=${task.id}`;

function TaskTable({
  rows,
  markReviewed,
  emptyLabel,
  dateColumnLabel,
  showType = false,
}: {
  rows: QaTaskRow[];
  markReviewed: boolean;
  emptyLabel: string;
  dateColumnLabel: string;
  showType?: boolean;
}) {
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>, row: QaTaskRow) => {
      if (!markReviewed) return; // read-only when viewing another user
      const to = taskUrl(row);
      if (e.metaKey || e.ctrlKey) {
        window.open(to, "_blank");
      } else {
        navigate(to);
      }
    },
    [navigate, markReviewed],
  );

  if (rows.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Table withTableBorder highlightOnHover={markReviewed} stickyHeader>
      <Table.Thead>
        <Table.Tr>
          {showType && <Table.Th>Type</Table.Th>}
          <Table.Th>Original Task ID</Table.Th>
          <Table.Th>Original Job ID</Table.Th>
          <Table.Th>{dateColumnLabel}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => {
          const { task, config } = row;
          return (
            <Table.Tr
              key={task.id}
              onClick={(e) => handleRowClick(e, row)}
              style={{ cursor: markReviewed ? "pointer" : "default" }}
            >
              {showType && <Table.Td>{config.typeLabel}</Table.Td>}
              <Table.Td style={{ textAlign: "left" }}>
                <Link
                  to={taskUrl(row)}
                  style={{ textDecoration: "underline" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(task.metadata?.source_task_id as string) ?? task.id}
                </Link>
              </Table.Td>
              <Table.Td>{task.metadata?.source_job_id as string}</Table.Td>
              <Table.Td>
                {new Date(task.created_at).toLocaleDateString()}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

interface RejectedQaTasksSectionProps {
  /** Lebron assignee value. Defaults to "me". Pass a user's email to view another user's tasks.
   *  Viewing another user requires the caller to be a lebron team lead or staff; a 403 shows a notice instead of tasks. */
  assignee?: string;
  /** Profile task type (user-insights key); picks which QA tasks the history
   *  tab lists. Needs Review always lists every QA type. */
  taskType: string;
}

export const RejectedQaTasksSection = ({
  assignee = "me",
  taskType,
}: RejectedQaTasksSectionProps) => {
  const viewingOther = assignee !== "me";
  const config = qaReviewConfigFor(taskType);
  const {
    rows: needsReview,
    count: needsReviewCount,
    isLoading: needsReviewLoading,
    isError: needsReviewError,
  } = useNeedsReview(assignee);
  const [allRejectedPage, setAllRejectedPage] = useState(1);
  const {
    data: allRejectedData,
    tasks: allRejectedTasks,
    isLoading: allRejectedLoading,
    isError: allRejectedError,
  } = useAllRejectedQaTasks(config, allRejectedPage, assignee);
  const totalPages = allRejectedData?.total_pages ?? 1;

  const isUnauthorized = needsReviewError || allRejectedError;

  return (
    <Card withBorder radius="lg" padding="lg" mt="xl" className="shadow-sm">
      <Text fz={18} fw={700} mb="md">
        QA Review
        {viewingOther && (
          <Text span fz={14} fw={400} c="dimmed" ml={8}>
            (read-only)
          </Text>
        )}
      </Text>

      {isUnauthorized ? (
        <Alert icon={<IconLock size={16} />} color="gray" variant="light">
          Not authorized to view this user&apos;s QA tasks.
        </Alert>
      ) : (
        <Tabs defaultValue="needs-review">
          <Tabs.List>
            <Tabs.Tab
              value="needs-review"
              leftSection={<IconAlertTriangle size={16} />}
              rightSection={
                !viewingOther && needsReviewCount > 0 ? (
                  <Badge size="sm" color="red" variant="filled" circle>
                    {needsReviewCount}
                  </Badge>
                ) : null
              }
            >
              Needs Review
            </Tabs.Tab>
            <Tabs.Tab
              value="all-rejected"
              leftSection={<IconListCheck size={16} />}
            >
              {config.historyLabel}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="needs-review" pt="md">
            {needsReviewLoading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <TaskTable
                rows={needsReview}
                markReviewed={!viewingOther}
                emptyLabel="Nothing needs review 🎉"
                dateColumnLabel="Flagged On"
                showType
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="all-rejected" pt="md">
            {allRejectedLoading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <>
                <TaskTable
                  rows={allRejectedTasks.map((task) => ({ task, config }))}
                  markReviewed={false}
                  emptyLabel={config.historyEmptyLabel}
                  dateColumnLabel={config.dateColumnLabel}
                />
                {totalPages > 1 && (
                  <div className="flex justify-center mt-6">
                    <Pagination
                      total={totalPages}
                      value={allRejectedPage}
                      onChange={setAllRejectedPage}
                      size="sm"
                    />
                  </div>
                )}
              </>
            )}
          </Tabs.Panel>
        </Tabs>
      )}
    </Card>
  );
};
